<template>
  <v-row>
    <v-col
      cols="12"
      sm="6"
    >
      <v-text-field
        v-model="first_name"
        :label="firstnameLabel"
        outlined
        :error-messages="getErrorMessages(v$.first_name)"
        @input="v$.first_name.$touch()"
        @blur="v$.first_name.$touch()"
      />
    </v-col>

    <v-col
      cols="12"
      sm="6"
    >
      <v-text-field
        v-model="middle_name"
        label="Middle Name"
        outlined
        :error-messages="getErrorMessages(v$.middle_name)"
        @input="v$.middle_name.$touch()"
        @blur="v$.middle_name.$touch()"
      />
    </v-col>

    <v-col
      cols="12"
      sm="6"
    >
      <v-text-field
        v-model="last_name"
        :label="lastnameLabel"
        outlined
        :error-messages="getErrorMessages(v$.last_name)"
        @input="v$.last_name.$touch()"
        @blur="v$.last_name.$touch()"
      />
    </v-col>

    <v-col
      cols="12"
      sm="6"
    >
      <v-autocomplete
        v-model="gender_id"
        :items="genderOptions"
        :label="genderLabel"
        outlined
        :error-messages="getErrorMessages(v$.gender_id)"
        @input="v$.gender_id.$touch()"
        @blur="v$.gender_id.$touch()"
      />
    </v-col>

    <BirthDateField
      class="sm6 xs12"
      :date="dob"
      outlined
      :required="required"
      :uncertain="uncertain_dob === true"
      :error-messages="getErrorMessages(v$.gender_id)"
      @update-date="newValue => updateBirthdateField(newValue)"
      @update-box="newValue => updateBirthdateBoxField(newValue)"
      @valid-changed="isValid => setBirthDateValidation(isValid)"
    />

    <v-col
      cols="12"
      sm="6"
    >
      <v-text-field
        ref="maskedField"
        v-model="a_number"
        v-mask="aNumMask"
        label="A#"
        outlined
        :error-messages="getErrorMessages(v$.a_number)"
        @input="v$.a_number.$touch()"
        @blur="v$.a_number.$touch()"
      />
    </v-col>

    <PhoneFields
      v-model="phone"
      :required="false"
    />

    <EmailFields
      v-model="email"
      :required="false"
    />
  </v-row>
</template>

<script>
import { helpers, required, requiredIf } from '@vuelidate/validators'
import { mapActions, mapGetters } from 'vuex'
import { useVuelidate } from '@vuelidate/core'

import { MASKS } from '@/utilities'

import BirthDateField from '@/components/Fields/BirthDate.vue'
import EmailFields from '@/components/Party/PartyEdit/SectionCards/PersonalDataCards/EmailFields.vue'
import PhoneFields from '@/components/Party/PartyEdit/SectionCards/PersonalDataCards/PhoneFields.vue'

export default {
  name: 'LegalCaseEditorPartyFields',

  components: {
    BirthDateField,
    EmailFields,
    PhoneFields,
  },

  props: {
    party: {
      type: Object,
      default: _ => ({}),
    },

    required: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      v$: useVuelidate(),
    }
  },

  data () {
    return {
      aNumMask: MASKS.ANUMBER,
      a_number: this.party.a_number,
      birthDateValid: false,
      dob: this.party.dob,
      email: this.party.email || { address: '' },
      first_name: this.party.first_name,
      gender_id: this.party.gender_id,
      last_name: this.party.last_name,
      middle_name: this.party.middle_name,
      phone: this.party.phone || { phone_number: '' },
      uncertain_dob: this.party.uncertain_dob,
    }
  },

  validations () {
    return {
      a_number: {
        format: helpers.withMessage(
          'Invalid A-number',
          value => !value || /^[\d]{9}$/.test((value || '').replaceAll('-', ''))
        ),
      },

      dob: { required: requiredIf(this.required && !this.uncertain_dob) },
      first_name: { required },
      gender_id: { required },
      last_name: { required },
      middle_name: {},
      uncertain_dob: {},
    }
  },

  computed: {
    compoundProperty () {
      return [
        this.a_number,
        this.dob,
        this.email,
        this.first_name,
        this.gender_id,
        this.last_name,
        this.middle_name,
        this.phone,
        this.uncertain_dob,
      ]
    },

    firstnameLabel () {
      return `First Name ${this.required ? '*' : ''}`
    },

    genderLabel () {
      return `Gender ${this.required ? '*' : ''}`
    },

    hasEmail () {
      return this.email.address || this.email.no_email
    },

    hasPhone () {
      return this.phone.country_prefix ||
        this.phone.phone_number ||
        this.phone.phone_type
    },

    lastnameLabel () {
      return `Last Name ${this.required ? '*' : ''}`
    },

    ...mapGetters([
      'genderOptions',
    ]),
  },

  watch: {
    compoundProperty: {
      deep: true,

      handler () {
        this.$emit('update', {
          ...this.party,
          a_number: this.a_number,
          dob: this.dob,
          email: this.hasEmail ? this.email : null,
          first_name: this.first_name,
          gender_id: this.gender_id,
          last_name: this.last_name,
          middle_name: this.middle_name,
          phone: this.hasPhone ? this.phone : null,
          uncertain_dob: this.uncertain_dob,
        })
      },

      immediate: true,
    },
  },

  created () {
    this.getGendersModel()
  },

  methods: {
    getErrorMessages (validator) {
      return validator?.$errors.map(e => e.$message)
    },

    setBirthDateValidation (isValid) {
      this.birthDateValid = isValid
    },

    updateBirthdateBoxField (newVal) {
      this.uncertain_dob = newVal
    },

    updateBirthdateField (newDate) {
      this.dob = newDate
    },

    ...mapActions([
      'getGendersModel',
    ]),
  },
}
</script>
